/**
 * Camera.ts
 * Scroll, pan, and zoom over the tile map. The camera tracks a world-space
 * top-left point and a zoom factor; apply() pushes that onto the world
 * container's transform. Panning and zooming are clamped to the map bounds so
 * you can never scroll off into empty space.
 */

import type { Container } from 'pixi.js';
import type { WorldPosition } from '../types';
import { CAMERA_ZOOM_MAX } from '../constants';

export class Camera {
  private zoom = 1;
  private minZoom = 0.18; // overridden by setZoomLimits() from the active map preset
  private maxZoom = CAMERA_ZOOM_MAX;
  private readonly position: WorldPosition = { x: 0, y: 0 }; // world coord at viewport top-left

  private viewportWidth = 1;
  private viewportHeight = 1;
  // World-space bounds the viewport is kept inside (may be negative for iso).
  private boundsX = 0;
  private boundsY = 0;
  private boundsW = 1;
  private boundsH = 1;

  constructor(private readonly world: Container) {}

  setZoomLimits(min: number, max: number = CAMERA_ZOOM_MAX): void {
    this.minZoom = Math.min(min, max);
    this.maxZoom = Math.max(min, max);
    this.zoom = this.clampZoom(this.zoom);
    this.clamp();
  }

  setViewport(width: number, height: number): void {
    this.viewportWidth = width;
    this.viewportHeight = height;
    this.clamp();
  }

  /** Set the world-space rectangle the camera may roam within. */
  setBounds(x: number, y: number, width: number, height: number): void {
    this.boundsX = x;
    this.boundsY = y;
    this.boundsW = width;
    this.boundsH = height;
    this.clamp();
  }

  /** Pan by a screen-space delta (pixels). World movement scales with zoom. */
  pan(dxScreen: number, dyScreen: number): void {
    this.position.x += dxScreen / this.zoom;
    this.position.y += dyScreen / this.zoom;
    this.clamp();
  }

  /** Centre the viewport on a world position. */
  centreOn(target: WorldPosition): void {
    this.position.x = target.x - this.viewportWidth / this.zoom / 2;
    this.position.y = target.y - this.viewportHeight / this.zoom / 2;
    this.clamp();
  }

  /** Zoom by a multiplicative factor, keeping the given screen point fixed. */
  zoomAt(factor: number, screenX: number, screenY: number): void {
    const before = this.screenToWorld(screenX, screenY);
    this.zoom = this.clampZoom(this.zoom * factor);
    const after = this.screenToWorld(screenX, screenY);
    // Shift so the world point under the cursor stays put.
    this.position.x += before.x - after.x;
    this.position.y += before.y - after.y;
    this.clamp();
  }

  /** Convert a viewport (screen) point to world space. */
  screenToWorld(screenX: number, screenY: number): WorldPosition {
    return {
      x: this.position.x + screenX / this.zoom,
      y: this.position.y + screenY / this.zoom,
    };
  }

  get currentZoom(): number {
    return this.zoom;
  }

  /** Set zoom while keeping the current viewport centre fixed in world space. */
  setZoomCentered(z: number): void {
    const cx = this.position.x + this.viewportWidth  / this.zoom / 2;
    const cy = this.position.y + this.viewportHeight / this.zoom / 2;
    this.zoom = this.clampZoom(z);
    this.position.x = cx - this.viewportWidth  / this.zoom / 2;
    this.position.y = cy - this.viewportHeight / this.zoom / 2;
    this.clamp();
  }

  get topLeft(): WorldPosition {
    return { x: this.position.x, y: this.position.y };
  }

  /** Apply the current camera transform to the world container. */
  apply(): void {
    this.world.scale.set(this.zoom);
    this.world.position.set(-this.position.x * this.zoom, -this.position.y * this.zoom);
  }

  /** Keep the viewport inside the bounds; centre an axis if bounds are smaller. */
  private clamp(): void {
    const viewW = this.viewportWidth / this.zoom;
    const viewH = this.viewportHeight / this.zoom;

    if (this.boundsW <= viewW) {
      this.position.x = this.boundsX + (this.boundsW - viewW) / 2;
    } else {
      const min = this.boundsX;
      const max = this.boundsX + this.boundsW - viewW;
      this.position.x = Math.min(Math.max(this.position.x, min), max);
    }

    if (this.boundsH <= viewH) {
      this.position.y = this.boundsY + (this.boundsH - viewH) / 2;
    } else {
      const min = this.boundsY;
      const max = this.boundsY + this.boundsH - viewH;
      this.position.y = Math.min(Math.max(this.position.y, min), max);
    }
  }

  private clampZoom(z: number): number {
    return Math.min(this.maxZoom, Math.max(this.minZoom, z));
  }
}
